import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, DollarSign, Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

interface TimeLogCardProps {
  log: {
    id: number;
    project_name: string;
    task_name?: string;
    user_name: string;
    description: string;
    start_time: string;
    end_time?: string;
    duration_minutes: number;
    is_billable: boolean;
    hourly_rate?: number;
  };
}

const TimeLogCard = ({ log }: TimeLogCardProps) => {
  const hours = (log.duration_minutes / 60).toFixed(2);
  const amount = log.is_billable && log.hourly_rate
    ? (log.duration_minutes / 60) * log.hourly_rate
    : 0;

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left Side - Main Info */}
          <div className="flex-1 space-y-2">
            {/* Project and Task */}
            <div>
              <h4 className="font-semibold">{log.project_name}</h4>
              {log.task_name && (
                <p className="text-sm text-muted-foreground">{log.task_name}</p>
              )}
            </div>

            {/* Description */}
            <p className="text-sm">{log.description}</p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(log.start_time), 'MMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>
                  {format(new Date(log.start_time), 'HH:mm')} - {log.end_time ? format(new Date(log.end_time), 'HH:mm') : 'ongoing'}
                </span>
              </div>
              <span>by {log.user_name}</span>
            </div>
          </div>

          {/* Right Side - Duration, Billable, Actions */}
          <div className="flex flex-col items-end gap-3">
            {/* Duration */}
            <div className="text-right">
              <div className="text-2xl font-bold">{hours}h</div>
              <div className="text-xs text-muted-foreground">{formatDuration(log.duration_minutes)}</div>
            </div>

            {/* Billable Badge and Amount */}
            <div className="flex flex-col items-end gap-1">
              <Badge variant={log.is_billable ? 'default' : 'secondary'}>
                {log.is_billable ? 'Billable' : 'Non-billable'}
              </Badge>
              {log.is_billable && amount > 0 && (
                <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                  <DollarSign className="h-3 w-3" />
                  <span>{amount.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeLogCard;
